import { useUser } from '@auth0/nextjs-auth0';
import { Option, Button } from '@material-tailwind/react';
import Select from '@material-tailwind/react/components/Select';
import { Field, Form, Formik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { LocationEnum, VirtualLocationType } from '../../../utils/enums';
import { UserInfo } from '../../../utils/interfaces';
import { LocationType, Place, VirtualLocation } from '../../../utils/types';
import * as Sentry from '@sentry/nextjs';

type Props = {
  showPlaceModal: boolean;
  setPlaceModal: (newLocationId?: number, isVirtual?: boolean) => void;
  newLocation: boolean;
  locationType: LocationType;
  placeId: number;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  setAlertMessage: React.Dispatch<React.SetStateAction<string>>;
};

function ToArray(enumme: any) {
  return Object.keys(enumme)
    .filter((key) => isNaN(Number(key)))
    .map((name) => ({
      label: name.replace(/([A-Z])/g, ' $1').trim(),
      value: enumme[name],
    }));
}

const virtualLocationOptions = ToArray(VirtualLocationType);

const PlacesModal: FC<Props> = ({
  showPlaceModal,
  setPlaceModal,
  newLocation,
  locationType,
  placeId,
  setShow,
  setAlertMessage,
}) => {
  const [physicalPlace, setPhysicalPlace] = useState<Place>({
    placeId: 0,
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    companyName: '',
    addressHash: '',
  });
  const [virtualPlace, setVirtualPlace] = useState<VirtualLocation>({
    virtualLocationId: 0,
    url: '',
    meetingId: '',
    phoneNumber: '',
    recipientPasscode: '',
    moderatorPasscode: '',
    virtualType: VirtualLocationType.Zoom,
    label: '',
  });

  const closeModal = (newLocationId?: number, isVirtual?: boolean) => {
    if (setPlaceModal !== undefined) {
      setPlaceModal(newLocationId, isVirtual);
    }
  };

  const { user } = useUser();
  const userInfo = user as UserInfo;

  useEffect(() => {
    async function fetchPersonalPhysical() {
      const res = await fetch(`/api/places/${placeId}`, {
        method: 'GET',
      });
      res.json().then((res: Place) => {
        setPhysicalPlace(res);
      });
    }
    async function fetchPersonalVirtual(virtualLocationId: number) {
      const res = await fetch(`/api/virtualLocations/${virtualLocationId}`, {
        method: 'GET',
      });
      res.json().then((res) => {
        setVirtualPlace(res);
      });
    }
    async function fetchTeamPhysical() {
      const res = await fetch(
        `/api/teams/${userInfo.accountDetails?.teamId}/places/${placeId}`,
        {
          method: 'GET',
        }
      );
      res.json().then((res) => {
        setPhysicalPlace(res);
      });
    }
    async function fetchTeamVirtual(virtualLocationId: number) {
      const res = await fetch(
        `/api/teams/${userInfo.accountDetails?.teamId}/virtuallocations/${virtualLocationId}`,
        {
          method: 'GET',
        }
      );
      res.json().then((res) => {
        setVirtualPlace(res);
      });
    }
    if (newLocation) {
      if (
        locationType === LocationEnum.PersonalPhysical ||
        locationType === LocationEnum.TeamPhysical
      ) {
        setPhysicalPlace({
          placeId: 0,
          name: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          postalCode: '',
          companyName: '',
          addressHash: '',
          teamId:
            locationType === LocationEnum.TeamPhysical
              ? userInfo.accountDetails?.teamId
              : undefined,
        });
      } else {
        setVirtualPlace({
          virtualLocationId: 0,
          url: '',
          meetingId: '',
          phoneNumber: '',
          recipientPasscode: '',
          moderatorPasscode: '',
          virtualType: VirtualLocationType.Zoom,
          label: '',
          teamId:
            locationType === LocationEnum.TeamVirtual
              ? userInfo.accountDetails?.teamId
              : undefined,
        });
      }
    } else {
      if (locationType === LocationEnum.PersonalPhysical) {
        fetchPersonalPhysical();
      } else if (locationType === LocationEnum.PersonalVirtual) {
        fetchPersonalVirtual(placeId);
      } else if (locationType === LocationEnum.TeamPhysical) {
        fetchTeamPhysical();
      } else if (locationType === LocationEnum.TeamVirtual) {
        fetchTeamVirtual(placeId);
      }
    }
  }, [newLocation, locationType, placeId]);

  const setHeaderTitle = (): any => {
    if (newLocation) {
      switch (locationType) {
        case LocationEnum.PersonalPhysical:
          return 'New Physical Location';
        case LocationEnum.PersonalVirtual:
          return 'New Virtual Location';
        case LocationEnum.TeamPhysical:
          return 'New Physical Team Location';
        case LocationEnum.TeamVirtual:
          return 'New Virtual Team Location';
      }
    } else {
      switch (locationType) {
        case LocationEnum.PersonalPhysical:
          return 'Edit Physical Location';
        case LocationEnum.PersonalVirtual:
          return 'Edit Virtual Location';
        case LocationEnum.TeamPhysical:
          return 'Edit Physical Team Location';
        case LocationEnum.TeamVirtual:
          return 'Edit Virtual Team Location';
      }
    }
  };

  if (!showPlaceModal) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
        <h1 className="text-lg font-semibold mb-4">{setHeaderTitle()}</h1>

        <div
          className={`${
            locationType === LocationEnum.PersonalVirtual ||
            locationType === LocationEnum.TeamVirtual
              ? 'hidden'
              : ''
          }`}
        >
          <p className="text-sm text-gray-700 mb-6">
            By including address or zip code, AI travel optimization will
            automatically be turned on for this location. (For editions with
            travel optimization)
          </p>
          <Formik
            enableReinitialize
            initialValues={physicalPlace}
            onSubmit={async (values, { setSubmitting }) => {
              const newPhysicalPlace: Place = {
                placeId: placeId,
                name: values.name!,
                address1: values.address1!,
                address2: values.address2!,
                city: values.city!,
                state: values.state!,
                postalCode: values.postalCode!,
                companyName: '',
                teamId: physicalPlace.teamId,
                addressHash:
                  physicalPlace?.addressHash !== undefined
                    ? physicalPlace?.addressHash
                    : '',
              };

              try {
                if (newPhysicalPlace.teamId) {
                  const response = await fetch(
                    `/api/teams/${newPhysicalPlace.teamId}/places`,
                    {
                      method: 'POST',
                      body: JSON.stringify(newPhysicalPlace),
                      headers: {
                        'Content-type': 'application/json;',
                      },
                    }
                  );
                  if (!response.ok) {
                    const errorBody = await response.text();
                    throw new Error(
                      `API call failed with status ${response.status}: ${errorBody}`
                    );
                  }
                  const savedPlace = await response.json();
                  setAlertMessage('Team Physical Location Saved');
                  setShow(true);
                  closeModal(savedPlace.placeId, false);
                } else {
                  const response = await fetch('/api/places', {
                    method: 'POST',
                    body: JSON.stringify(newPhysicalPlace),
                    headers: {
                      'Content-type': 'application/json;',
                    },
                  });
                  if (!response.ok) {
                    const errorBody = await response.text();
                    throw new Error(
                      `API call failed with status ${response.status}: ${errorBody}`
                    );
                  }
                  const savedPlace = await response.json();
                  setAlertMessage('Physical Location Saved');
                  setShow(true);
                  closeModal(savedPlace.placeId, false);
                }
              } catch (err) {
                Sentry.captureException(err);
                console.log(err);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form className="flex flex-col gap-4">
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="name"
                    placeholder="Place Name"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="address1"
                    placeholder="Address 1"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="address2"
                    placeholder="Address 2"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="city"
                    placeholder="City"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="state"
                    placeholder="State"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="postalCode"
                    placeholder="Postal Code"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    type="button"
                    onClick={() => closeModal()}
                    className="px-5 py-2 rounded-2xl text-[12px] font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2 rounded-2xl text-[12px] font-semibold bg-teal-600 text-white hover:bg-teal-700 disabled:bg-teal-400 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        <div
          className={`${
            locationType === LocationEnum.PersonalPhysical ||
            locationType === LocationEnum.TeamPhysical
              ? 'hidden'
              : ''
          }`}
        >
          <Formik
            enableReinitialize
            initialValues={virtualPlace}
            onSubmit={async (values, { setSubmitting }) => {
              const newVirtualPlace: VirtualLocation = {
                virtualLocationId: placeId,
                url: values.url!,
                meetingId: values.meetingId!,
                phoneNumber: values.phoneNumber!,
                recipientPasscode: values.recipientPasscode!,
                moderatorPasscode: values.moderatorPasscode!,
                virtualType: values.virtualType!,
                label: values.label!,
                teamId: virtualPlace.teamId,
              };

              try {
                if (newVirtualPlace.teamId) {
                  const response = await fetch(
                    `/api/teams/${newVirtualPlace.teamId}/virtuallocations`,
                    {
                      method: 'POST',
                      body: JSON.stringify(newVirtualPlace),
                      headers: {
                        'Content-type': 'application/json;',
                      },
                    }
                  );
                  if (!response.ok) {
                    const errorBody = await response.text();
                    throw new Error(
                      `API call failed with status ${response.status}: ${errorBody}`
                    );
                  }
                  const savedVirtualPlace = await response.json();
                  setAlertMessage('Virtual Team Location Saved');
                  setShow(true);
                  closeModal(savedVirtualPlace.virtualLocationId, true);
                } else {
                  const response = await fetch('/api/virtualLocations', {
                    method: 'POST',
                    body: JSON.stringify(newVirtualPlace),
                    headers: {
                      'Content-type': 'application/json;',
                    },
                  });
                  if (!response.ok) {
                    const errorBody = await response.text();
                    throw new Error(
                      `API call failed with status ${response.status}: ${errorBody}`
                    );
                  }
                  const savedVirtualPlace = await response.json();
                  setAlertMessage('Virtual Location Saved');
                  setShow(true);
                  closeModal(savedVirtualPlace.virtualLocationId, true);
                }
              } catch (err) {
                Sentry.captureException(err);
                console.log(err);
                setSubmitting(false);
              }
            }}
          >
            {(props) => (
              <Form className="flex flex-col gap-4">
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="label"
                    placeholder="Name"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <SelectField
                    value={props.values?.virtualType}
                    name="virtualType"
                    setFieldValue={props.setFieldValue}
                    options={virtualLocationOptions}
                    label="Type"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="url"
                    placeholder="URL"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="meetingId"
                    placeholder="Meeting ID"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="phoneNumber"
                    placeholder="Dial-in Phone"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="recipientPasscode"
                    placeholder="Recipient Passcode"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <Field
                    as="input"
                    type="text"
                    name="moderatorPasscode"
                    placeholder="Moderator Passcode"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    type="button"
                    onClick={() => closeModal()}
                    className="px-5 py-2 rounded-2xl text-[12px] font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={props.isSubmitting}
                    className="px-5 py-2 rounded-2xl text-[12px] font-semibold bg-teal-600 text-white hover:bg-teal-700 disabled:bg-teal-400 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default PlacesModal;

const SelectField = ({ name, value, label, setFieldValue, options }: any) => {
  return (
    <Select
      color="teal"
      label={label}
      value={isNaN(value) || value === null ? value : value.toString()}
      onChange={(val) => {
        setFieldValue(name, val);
      }}
      className="w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
    >
      {options.map((option: any, idx: number) => {
        const optionLabel =
          typeof option === 'string'
            ? option.replace(/([A-Z])/g, ' $1').trim()
            : option.label;

        const optionValue =
          typeof option === 'string' ? idx.toString() : option.value.toString();

        return (
          <Option key={optionValue} value={optionValue}>
            {optionLabel}
          </Option>
        );
      })}
    </Select>
  );
};
